export const checkoutSlugs = ["start-monthly", "start-annual", "growth-monthly", "growth-annual", "pro-monthly", "pro-annual"] as const;
export type CheckoutSlug = (typeof checkoutSlugs)[number];
export function parseCheckoutRequest(value: unknown): CheckoutSlug | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const input = value as Record<string, unknown>;
  if (Object.keys(input).length !== 1 || typeof input.plan !== "string") return null;
  return checkoutSlugs.includes(input.plan as CheckoutSlug) ? input.plan as CheckoutSlug : null;
}
export function checkoutErrorMessage(code: string) {
  if (code === "CHECKOUT_EXPIRED") return "This checkout has expired. Choose your plan again to start a new checkout.";
  if (code === "CHECKOUT_AUTH_REQUIRED") return "Sign in to link a subscription to your business and choose your plan in Billing.";
  if (code === "CHECKOUT_WORKSPACE_REQUIRED") return "Complete your business setup before choosing a paid plan.";
  if (code === "CHECKOUT_BILLING_REQUIRED") return "Manage the plan for this business from Billing in your workspace.";
  if (code === "RATE_LIMITED") return "Too many attempts. Please wait a few minutes and try again.";
  if (code === "CHECKOUT_FORBIDDEN") return "The workspace owner needs to manage this subscription.";
  return "Online checkout is temporarily unavailable. Please try again later or contact us.";
}
export function checkoutRecoveryLinks(signInHref: string) {
  // Keep relative application links relative; never assume a staging hostname.
  const relative = signInHref.startsWith("/") && !signInHref.startsWith("//");
  const base = new URL(signInHref, "https://optrixis.invalid");
  base.searchParams.set("next", "/app/billing");
  return {
    signIn: relative ? `${base.pathname}${base.search}${base.hash}` : base.toString(),
    workspace: relative ? "/app/billing" : new URL("/app/billing", base).toString(),
  };
}
export function isStripeCheckoutUrl(value: unknown): value is string {
  if (typeof value !== "string") return false;
  try { const url = new URL(value); return url.protocol === "https:" && url.hostname === "checkout.stripe.com" && !url.username && !url.password && !url.port; } catch { return false; }
}
export async function requestCheckout(plan: CheckoutSlug, idempotencyKey: string, send: typeof fetch = fetch) {
  const response = await send("/api/billing/checkout", {
    method: "POST", headers: { "content-type": "application/json", "idempotency-key": idempotencyKey },
    body: JSON.stringify({ plan }), signal: AbortSignal.timeout(45_000),
  });
  const payload = await response.json().catch(() => null) as { url?: string; error?: string } | null;
  if (!response.ok) throw new Error(payload?.error || "CHECKOUT_UNAVAILABLE");
  if (!isStripeCheckoutUrl(payload?.url)) throw new Error("CHECKOUT_UNAVAILABLE");
  return payload.url;
}
